import React, { useEffect, useMemo, useState } from 'react';
import {
  DropDown,
  useTheme,
  withTheme,
} from '@canopysecurity/component-library';
import styled from 'styled-components';
import {
  ParagraphText,
  SectionTitleText,
} from '../../StyledComponents/Typography';
import CondorSpinner from '../CondorSpinner';
import pixelRatio from '@canopysecurity/component-library/utils/pixelRatio';
import {
  HomeBaseTableRow,
  TableContainer,
  TableRows,
} from '../../StyledComponents/Table';
import { useQuery } from '@tanstack/react-query';
import {
  getArtifactGroups,
  GroupedArtifact,
} from '../../services/ArtifactService';

const DropdownContainer = styled.div``;

const ArtifactTableContainer = styled.div`
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.5);
  padding: 1% 2% 1% 2%;
  height: 80%;
  overflow-y: hidden;
`;

const ArtifactTableRow = styled(HomeBaseTableRow)`
  grid-template-columns: 25fr 25fr 20fr 15fr 15fr;
`;

const ArtifactTableHeaderRow = withTheme(styled(ArtifactTableRow)`
  display: grid;
  background-color: ${({ theme }) => theme.background.main};
`);

const ArtifactTableHeader = [
  'ARTIFACT NAME',
  'ARTIFACT TYPE',
  'LAST RELEASED IN',
  'LAST UPDATED',
  'DATE CREATED',
];

export function ArtifactTable() {
  const theme = useTheme();

  const { data: artifacts, isPending: artifactsLoading } = useQuery({
    queryKey: ['artifacts'],
    queryFn: getArtifactGroups,
  });

  const groupNames = useMemo(() => {
    return ((artifacts ?? []).filter(Boolean) as GroupedArtifact[])
      ?.map((group) => group.typeName)
      .filter((g): g is string => !!g);
  }, [artifacts]);

  const [selectedArtifactGroupType, setSelectedArtifactGroupType] = useState(
    groupNames?.[0] ?? 'ota-app',
  );
  useEffect(() => {
    if (groupNames?.length) {
      setSelectedArtifactGroupType(groupNames[0]);
    }
  }, [groupNames]);

  const selectedGroupIndex = useMemo(() => {
    return artifacts?.findIndex(
      (group: any) => group.typeName === selectedArtifactGroupType,
    );
  }, [artifacts, selectedArtifactGroupType]);

  if (artifactsLoading) {
    return <CondorSpinner />;
  }

  return (
    <TableContainer>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingBottom: '16px',
        }}
      >
        <SectionTitleText>Artifacts</SectionTitleText>
        <div style={{ marginLeft: '8px' }}>
          <DropdownContainer>
            <DropDown
              items={groupNames}
              onValueChange={(value: string) =>
                setSelectedArtifactGroupType(value)
              }
              placeholderText={'Select Artifact Group'}
              dropdownListContainerStyle={{
                width: pixelRatio.widthPercentageToDP('10'),
                zIndex: 99,
              }}
              selectedValue={selectedArtifactGroupType}
            />
          </DropdownContainer>
        </div>
      </div>
      <ArtifactTableContainer>
        <ArtifactTableHeaderRow>
          {ArtifactTableHeader.map((header) => (
            <SectionTitleText key={header}>{header}</SectionTitleText>
          ))}
        </ArtifactTableHeaderRow>
        <TableRows>
          {artifacts?.[selectedGroupIndex ?? 0]?.artifacts?.map(
            (artifact: any, index: number) => {
              return (
                <ArtifactTableRow
                  key={index}
                  style={{
                    backgroundColor:
                      index % 2 === 0
                        ? theme.secondary.darker
                        : theme.background.surface,
                  }}
                >
                  <ParagraphText style={{ alignSelf: 'center' }}>
                    {artifact.semver}
                  </ParagraphText>
                  <ParagraphText style={{ alignSelf: 'center' }}>
                    {artifact.typeName}
                  </ParagraphText>
                  <ParagraphText style={{ alignSelf: 'center' }}>
                    {artifact.lastReleaseVersion ?? '-'}
                  </ParagraphText>
                  <ParagraphText style={{ alignSelf: 'center' }}>
                    {new Date(artifact.lastUpdateTime).toLocaleDateString()}
                  </ParagraphText>
                  <ParagraphText style={{ alignSelf: 'center' }}>
                    {new Date(artifact.createTime).toLocaleDateString()}
                  </ParagraphText>
                </ArtifactTableRow>
              );
            },
          )}
        </TableRows>
      </ArtifactTableContainer>
    </TableContainer>
  );
}
